/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { ref, computed } from 'vue'
import type { PasswordProtection } from '../stores/strapi/types'

// Prefix everything so we never collide
const STORAGE_PREFIX = 'myApp_'
const GLOBAL_KEY = `${STORAGE_PREFIX}globalAuth_`

export const usePasswordAuth = () => {
  // Reactive state
  const globalAuthenticated = ref(false)
  const authenticatedPages = ref<Set<string>>(new Set())
  const authenticatedPosts = ref<Set<string>>(new Set())

  // ---------------------------------------------------------------------------
  // 1. Helper: does the content actually require a password?
  // ---------------------------------------------------------------------------
  const isGlobalPasswordRequired = (cfg?: PasswordProtection | null): boolean => {
    return cfg?.passwordProtected === true
  }

  const isPagePasswordRequired = (cfg?: PasswordProtection | null): boolean => {
    return cfg?.passwordProtected === true
  }

  const isPostPasswordRequired = (cfgList?: PasswordProtection[] | null): boolean => {
    return Array.isArray(cfgList) && cfgList.some(p => p.passwordProtected)
  }

  // ---------------------------------------------------------------------------
  // 2. Core guard: should I show?
  // ---------------------------------------------------------------------------
  const shouldShowContent = (
    siteCfg?: PasswordProtection | null,
    pageOrPostCfg?: PasswordProtection | PasswordProtection[] | null,
    slug?: string,
    isPost = false
  ): boolean => {
    // If a global password is required but NOT yet passed, hide everything
    if (isGlobalPasswordRequired(siteCfg) && !globalAuthenticated.value) {
      return false
    }

    // If there's a slug and some password config, enforce page/post check
    if (slug && pageOrPostCfg != null) {
      if (isPost) {
        if (
          isPostPasswordRequired(pageOrPostCfg as PasswordProtection[]) &&
          !authenticatedPosts.value.has(slug)
        ) {
          return false
        }
      } else {
        if (
          isPagePasswordRequired(pageOrPostCfg as PasswordProtection) &&
          !authenticatedPages.value.has(slug)
        ) {
          return false
        }
      }
    }

    return true
  }

  // ---------------------------------------------------------------------------
  // Authenticate functions (set both reactive Set AND sessionStorage)
  // ---------------------------------------------------------------------------
  const authenticateGlobal = (): void => {
    globalAuthenticated.value = true
    sessionStorage.setItem(GLOBAL_KEY, 'true')
  }

  const authenticatePage = (slug: string): void => {
    if (!slug) return
    authenticatedPages.value.add(slug)
    sessionStorage.setItem(`${STORAGE_PREFIX}pageAuth_${slug}`, 'true')
  }

  const authenticatePost = (slug: string): void => {
    if (!slug) return
    authenticatedPosts.value.add(slug)
    sessionStorage.setItem(`${STORAGE_PREFIX}postAuth_${slug}`, 'true')
  }

  // ---------------------------------------------------------------------------
  // Clear functions
  // ---------------------------------------------------------------------------
  const clearAllAuth = (): void => {
    globalAuthenticated.value = false
    authenticatedPages.value.clear()
    authenticatedPosts.value.clear()

    Object.keys(sessionStorage).forEach(key => {
      if (
        key === GLOBAL_KEY ||
        key.startsWith(`${STORAGE_PREFIX}pageAuth_`) ||
        key.startsWith(`${STORAGE_PREFIX}postAuth_`)
      ) {
        sessionStorage.removeItem(key)
      }
    })
  }

  const clearPageAuth = (slug: string): void => {
    authenticatedPages.value.delete(slug)
    sessionStorage.removeItem(`${STORAGE_PREFIX}pageAuth_${slug}`)
  }

  const clearPostAuth = (slug: string): void => {
    authenticatedPosts.value.delete(slug)
    sessionStorage.removeItem(`${STORAGE_PREFIX}postAuth_${slug}`)
  }

  // ---------------------------------------------------------------------------
  // Check authentication status
  // ---------------------------------------------------------------------------
  const isGlobalAuthenticated = computed(() => globalAuthenticated.value)

  const isPageAuthenticated = (slug: string): boolean => {
    return authenticatedPages.value.has(slug)
  }

  const isPostAuthenticated = (slug: string): boolean => {
    return authenticatedPosts.value.has(slug)
  }

  // ---------------------------------------------------------------------------
  // Password validation (client-side ONLY for "dummy gate")
  // ---------------------------------------------------------------------------
  // **NOTE: THIS IS INSECURE** if these are the real passwords.
  const validatePagePassword = (cfg: PasswordProtection, input: string): boolean => {
    return cfg.passwordProtected && cfg.password.trim() === input.trim()
  }

  const validatePostPassword = (cfgList: PasswordProtection[], input: string): boolean => {
    return cfgList
      .filter(p => p.passwordProtected)
      .some(p => p.password.trim() === input.trim())
  }

  // ---------------------------------------------------------------------------
  // Build initial state from sessionStorage in one pass
  // ---------------------------------------------------------------------------
  const restoreFromSession = (): void => {
    // Check global flag first
    if (sessionStorage.getItem(GLOBAL_KEY) === 'true') {
      globalAuthenticated.value = true
    }

    // Walk through all keys once
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (!key) continue

      if (key.startsWith(`${STORAGE_PREFIX}pageAuth_`)) {
        const slug = key.slice(`${STORAGE_PREFIX}pageAuth_`.length)
        if (sessionStorage.getItem(key) === 'true') {
          authenticatedPages.value.add(slug)
        }
      }

      if (key.startsWith(`${STORAGE_PREFIX}postAuth_`)) {
        const slug = key.slice(`${STORAGE_PREFIX}postAuth_`.length)
        if (sessionStorage.getItem(key) === 'true') {
          authenticatedPosts.value.add(slug)
        }
      }
    }
  }

  // Immediately restore on composable initialisation
  restoreFromSession()

  return {
    // Reactive state
    isGlobalAuthenticated,
    authenticatedPages: computed(() => authenticatedPages.value),
    authenticatedPosts: computed(() => authenticatedPosts.value),

    // Guard
    shouldShowContent,

    // Checkers
    isGlobalPasswordRequired,
    isPagePasswordRequired,
    isPostPasswordRequired,
    isPageAuthenticated,
    isPostAuthenticated,

    // Auth action methods
    authenticateGlobal,
    authenticatePage,
    authenticatePost,

    // Clearing
    clearAllAuth,
    clearPageAuth,
    clearPostAuth,

    // Validation (client-side only!)
    validatePagePassword,
    validatePostPassword,
  }
}
